import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';

import { CloseIcon } from '../sidebars/sidebarElements/SidebarTemplate';
import { useProject } from '@contexts/Project.context';
import { useProjectMode } from '@contexts/ProjectMode.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { PROJECT_VIEW_MODE } from '@utils/constants';
import { Container, OuterContainer, StyledCheckbox, StyledHeader } from './SliderTool';

/**
 * Returns a numeric detection count from a feature's `detections` property,
 * supporting both the legacy shape (features array) and the collection shape
 * (classes with per-object counts).
 */
const getCount = detections => {
	if (!detections) return 0;
	if (detections.features?.length) return detections.features.length;
	if (detections.classes?.length) {
		return detections.classes.reduce((sum, c) => sum + (c.count ?? 0), 0);
	}
	return 0;
};

const SortSingleImage = ({ tools, setTools }) => {
	const { filteredImages, pickedModelClasses, dispatch } = useProject();
	const collectionObjects = useCollectionStore(s => s.objects);
	const projectViewMode = useProjectMode();

	const DETECTIONS_ASCENDING = 'Detections ascending';
	const DETECTIONS_DESCENDING = 'Detections descending';

	const [sortingRun, setSortingRun] = useState(0);

	const [sortOptions, setSortOptions] = useState([
		{ name: DETECTIONS_ASCENDING, isActive: false },
		{ name: DETECTIONS_DESCENDING, isActive: false },
	]);

	useEffect(() => {
		if (sortOptions.some(option => option.isActive) && sortingRun === 0) {
			const activeSortingOption = sortOptions.filter(option => option.isActive)[0];
			dispatch({
				type: 'setFilteredImages',
				payload: sortImages(activeSortingOption.name),
			});
			setSortingRun(1);
		} else {
			setSortingRun(0);
		}
	}, [filteredImages]);

	// model mode: reset sort state when model classes change
	useEffect(() => {
		if (projectViewMode !== PROJECT_VIEW_MODE.MODEL) return;
		if (pickedModelClasses?.length > 0) {
			setSortOptions(
				sortOptions.map(sortOption => ({
					...sortOption,
					isActive: false,
				}))
			);
		}
	}, [pickedModelClasses]);

	// collection mode: reset sort state when collection objects change
	useEffect(() => {
		if (projectViewMode !== PROJECT_VIEW_MODE.COLLECTION) return;
		if (collectionObjects?.length > 0) {
			setSortOptions(
				sortOptions.map(sortOption => ({
					...sortOption,
					isActive: false,
				}))
			);
		}
	}, [collectionObjects]);

	const closeSort = () => setTools(tools.map(tool => ({ ...tool, isActive: false })));

	const handleSortImages = e => {
		const sortOptionChosen = e.target.value;
		dispatch({
			type: 'setFilteredImages',
			payload: sortImages(sortOptionChosen),
		});
		setSortOptions(
			sortOptions.map(sortOption =>
				sortOption.name === sortOptionChosen
					? { ...sortOption, isActive: !sortOption.isActive }
					: { ...sortOption, isActive: false }
			)
		);
	};

	const sortImages = sortOption => {
		return filteredImages.toSorted((a, b) => {
			const countA = getCount(a.get('detections'));
			const countB = getCount(b.get('detections'));

			if (countA === countB) return 0;
			if (countA === 0) return 1;
			if (countB === 0) return -1;

			switch (sortOption) {
				case DETECTIONS_ASCENDING:
					return countA - countB;
				case DETECTIONS_DESCENDING:
					return countB - countA;
				default:
					return 0;
			}
		});
	};

	if (!tools.filter(tool => tool.name === 'sorting')[0]?.isActive) {
		return null;
	}

	return (
		<OuterContainer>
			<Container>
				<StyledHeader>
					<h2>Sort Images</h2>
					<CloseIcon onClick={closeSort} role="button" />
				</StyledHeader>
				<Form>
					{sortOptions?.map((sortOption, index) => (
						<StyledCheckbox
							type="radio"
							name="sortGroup"
							key={sortOption.name}
							defaultChecked={sortOption.isActive}
							label={sortOption.name}
							value={sortOption.name}
							onChange={handleSortImages}
							id={`sort-type-${index}`}
						/>
					))}
				</Form>
			</Container>
		</OuterContainer>
	);
};

export default SortSingleImage;
